/**
 * MODULE: _shared/frontend-shell/src/services/langflowAssistantFlow.ts
 * PURPOSE: Resolve shell AI Assistant Langflow **flow id** once per module load via **`GET /api/v1/platform/langflow/flows`** (gateway JWT).
 * RECENT: 2026-06-12 [2.2.1367] — HTTP **429** on **`GET /api/v1/platform/langflow/flows`** maps to quota exceeded message (not Langflow config hints).
 * RECENT: 2026-04-27 — [2.2.703] Gateway **`GET /api/v1/platform/langflow/flows`** contract unchanged — root **`CHANGELOG` [2.2.703]**. 2026-04-26 — generation guard on tenant reset; reject unwired ids; optional **`VITE_LANGFLOW_ASSISTANT_FLOW_NAME`** — root **`CHANGELOG` [2.2.702]**.
 */

import { getApiBaseUrl, getAuthHeaders } from '@shared/lib/frontend/api/config';

import { quotaExceededMessageFromHttpStatus } from '../components/assistant/assistantQuotaGuidance';
import { isUnwiredLangflowFlowId } from './langflowFlowId';

const DEFAULT_ASSISTANT_FLOW_NAME = 'Perplexity Assistant';

let _flowIdPromise: Promise<string> | null = null;
/** Bumped on **`resetAssistantLangflowFlowIdCache`** so a stale in-flight fetch cannot clear a newer cache entry. */
let _flowResolveGeneration = 0;

export function resetAssistantLangflowFlowIdCache(): void {
  _flowIdPromise = null;
  _flowResolveGeneration += 1;
}

function assistantFlowName(): string {
  const fromEnv = (import.meta.env.VITE_LANGFLOW_ASSISTANT_FLOW_NAME as string | undefined)?.trim();
  return fromEnv || DEFAULT_ASSISTANT_FLOW_NAME;
}

interface LangflowFlowsListResponse {
  flows?: Array<{ id?: string; name?: string | null; description?: string | null }>;
  count?: number;
  base_url?: string;
}

/**
 * Returns the same promise for the lifetime of the loaded JS module (until **`resetAssistantLangflowFlowIdCache`**).
 * Not called on app boot — callers invoke when the assistant panel needs a flow id without **`VITE_LANGFLOW_FLOW_ID`**.
 */
export function getAssistantLangflowFlowId(): Promise<string> {
  if (_flowIdPromise) return _flowIdPromise;

  const name = assistantFlowName();
  const generationAtStart = _flowResolveGeneration;

  _flowIdPromise = (async () => {
    const url = `${getApiBaseUrl().replace(/\/$/, '')}/api/v1/platform/langflow/flows`;
    const res = await fetch(url, { headers: getAuthHeaders() });
    const raw = await res.text();
    let data: LangflowFlowsListResponse = {};
    try {
      data = raw ? (JSON.parse(raw) as LangflowFlowsListResponse) : {};
    } catch {
      throw new Error(`Langflow flows list: invalid JSON (${res.status})`);
    }
    if (!res.ok) {
      if (res.status === 429) {
        let quotaBody: Record<string, unknown> = {};
        try {
          quotaBody = raw ? (JSON.parse(raw) as Record<string, unknown>) : {};
        } catch {
          quotaBody = {};
        }
        const quotaMsg = quotaExceededMessageFromHttpStatus(res.status, quotaBody);
        if (quotaMsg) {
          throw new Error(quotaMsg);
        }
      }
      const detail =
        typeof (data as { detail?: unknown }).detail === 'string'
          ? (data as { detail: string }).detail
          : raw.slice(0, 400);
      throw new Error(detail || `Langflow flows list failed: HTTP ${res.status}`);
    }
    const flows = Array.isArray(data.flows) ? data.flows : [];
    const flow = flows.find((f) => (f.name ?? '').trim() === name);
    if (!flow?.id) {
      throw new Error(`Flow "${name}" not found in Langflow (listed ${flows.length} flow(s)).`);
    }
    const id = String(flow.id).trim();
    if (isUnwiredLangflowFlowId(id)) {
      throw new Error(`Flow "${name}" resolved to an unwired / placeholder id; pick a real flow in Langflow.`);
    }
    return id;
  })().catch((err) => {
    if (generationAtStart === _flowResolveGeneration) {
      _flowIdPromise = null;
    }
    throw err;
  });

  return _flowIdPromise;
}
